import { useCallback, useMemo } from 'react'
import {
  useUpdateScheduleEntry,
  useUpsertBreak,
  type TechnicianScheduleView,
} from '../../../entities/schedule'
import { combineDayAndTimeIso } from '../../../shared/helpers/date'
import { rawBoardMinutes } from '../../../shared/lib/schedule'
import { findConflicts, isBlocked } from './dayConflicts'
import { usePauseEditState, type PauseEditing } from './pauseEditState'
import { describeRejection } from './rejectionMessage'
import type { PauseDayContext, PauseTapInfo } from './types'

/**
 * One finished gesture, in the shape the server wants it.
 *
 * A break is upserted by technician and day — that is the only route that can move a break the
 * server merely projected, which has no row yet. An absence moves through `PATCH /schedule/:id`
 * like every other block on every other calendar: per `apps/web/CLAUDE.md`, `/schedule/*` changes
 * *when* something is, and the owner route changes *what it is*. This used to go to
 * `PATCH /absences/:id`, which is the owner route — so it paid for a full owner update, invalidated
 * six query roots, and had no optimistic path, which is why the block visibly snapped back.
 *
 * Neither carries an override field. This feature cannot double-book or book out of hours at all.
 */
type MoveTarget =
  | { kind: 'break'; technicianId: number; startIso: string }
  | { kind: 'absence'; entryId: number; startIso: string; endIso: string }

/**
 * Moving a Pause or Abwesenheit on the worker calendar: the drop/tap handlers, the conflict gate,
 * the popover state, and the two mutations — everything except the pixels.
 *
 * The widget stays the owner of the drag primitive and of `buildDayBlocks`; this hook sees the day
 * only through `dayBlocksFor`, in the structural `PauseDayContext` shape. That keeps the FSD
 * direction clean (feature imports nothing from the widget) while both sides keep checking against
 * the very blocks the calendar draws.
 */
export function usePauseReschedule(args: {
  /** The buffered snapshot the widget is drawing — the same data every drop must check against. */
  technicianData: TechnicianScheduleView | null
  /** Rebuilds the day exactly as the calendar draws it; called per gesture and per popover edit. */
  dayBlocksFor: (dayIso: string) => PauseDayContext
}) {
  const { technicianData, dayBlocksFor } = args

  const upsertBreak = useUpsertBreak()
  const updateEntry = useUpdateScheduleEntry()
  const pauseEdit = usePauseEditState()

  /** Minutes from the window start, unclamped — what `findConflicts` compares. */
  const toRange = useCallback(
    (startIso: string, endIso: string, dayIso: string) => ({
      startMin: rawBoardMinutes(startIso, dayIso),
      endMin: rawBoardMinutes(endIso, dayIso),
    }),
    [],
  )

  /**
   * Sends one finished move. No overrides, ever — see `isBlocked`.
   *
   * `mutateAsync`, not `mutate`, so a rejection is catchable at all: `mutate` would bury it in
   * `isError`, where nothing reads it, which is precisely how a refused drop used to look like it
   * had simply not taken.
   *
   * `reopenOnReject` is what to put back on screen if the server refuses. A clean drag commits
   * without ever opening the popover, so on failure there is nothing on screen to carry the
   * message — this re-opens it at the attempted time, with the reason already in place.
   */
  const commitMove = useCallback(
    async (target: MoveTarget, reopenOnReject: PauseEditing) => {
      try {
        if (target.kind === 'break') {
          await upsertBreak.mutateAsync({
            technicianId: target.technicianId,
            startsAt: target.startIso,
          })
        } else {
          await updateEntry.mutateAsync({
            entryId: target.entryId,
            dto: { slot: { kind: 'SPAN', startsAt: target.startIso, endsAt: target.endIso } },
          })
        }
        pauseEdit.close()
      } catch (error) {
        pauseEdit.open({ ...reopenOnReject, rejection: describeRejection(error) })
      }
    },
    [upsertBreak, updateEntry, pauseEdit],
  )

  const handleDrop = useCallback(
    (type: string, dayIso: string, hours: number, minutes: number, entryId?: number, anchorRect?: DOMRect) => {
      const td = technicianData
      if (!td) return

      const day = dayBlocksFor(dayIso)
      const startIso = combineDayAndTimeIso(dayIso, hours, minutes)
      const startMs = new Date(startIso).getTime()

      if (type === 'break') {
        // The real break length, from the server's own entry rather than an assumed 30 minutes.
        const durationMin = day.breakBlock?.durationMinutes ?? td.breakDurationMinutes
        const endIso = new Date(startMs + durationMin * 60_000).toISOString()
        const advisory = findConflicts(toRange(startIso, endIso, dayIso), day, 'BREAK')
        const editing: PauseEditing = {
          kind: 'break',
          dayIso,
          startIso,
          endIso,
          anchorRect: anchorRect ?? new DOMRect(),
          excludeKey: 'BREAK',
          ...advisory,
        }

        // A clean drop just lands. A refused one opens the popover at the attempted time, where
        // the reason sits next to the fields and another time can be picked.
        if (isBlocked(advisory)) {
          pauseEdit.open(editing)
          return
        }
        void commitMove(
          { kind: 'break', technicianId: Number(td.technicianId), startIso },
          editing,
        )
        return
      }

      if (type === 'tm' && entryId !== undefined) {
        const entry = td.timeManagementEntries.find((e) => e.id === entryId)
        if (!entry) return
        // A move keeps the length; only the anchor changes.
        const duration = new Date(entry.endsAt).getTime() - new Date(entry.startsAt).getTime()
        const endIso = new Date(startMs + duration).toISOString()
        const excludeKey = `ABSENCE:${entryId}`
        const advisory = findConflicts(toRange(startIso, endIso, dayIso), day, excludeKey)
        const editing: PauseEditing = {
          kind: 'tm',
          id: entryId,
          dayIso,
          startIso,
          endIso,
          anchorRect: anchorRect ?? new DOMRect(),
          excludeKey,
          ...advisory,
        }

        if (isBlocked(advisory)) {
          pauseEdit.open(editing)
          return
        }
        void commitMove({ kind: 'absence', entryId, startIso, endIso }, editing)
      }
    },
    [technicianData, dayBlocksFor, toRange, commitMove, pauseEdit],
  )

  const handleTap = useCallback(
    ({ type, dayIso, entryId, anchorRect }: PauseTapInfo) => {
      const td = technicianData
      if (!td) return
      const day = dayBlocksFor(dayIso)

      if (type === 'break') {
        if (!day.breakBlock) return
        const { entry, durationMinutes } = day.breakBlock
        const startIso = entry.startsAt
        const endIso = new Date(new Date(startIso).getTime() + durationMinutes * 60_000).toISOString()
        pauseEdit.open({
          kind: 'break',
          dayIso,
          startIso,
          endIso,
          anchorRect,
          excludeKey: 'BREAK',
          ...findConflicts(toRange(startIso, endIso, dayIso), day, 'BREAK'),
        })
        return
      }

      if (type === 'tm' && entryId !== undefined) {
        const entry = td.timeManagementEntries.find((e) => e.id === entryId)
        if (!entry) return
        const excludeKey = `ABSENCE:${entryId}`
        pauseEdit.open({
          kind: 'tm',
          id: entryId,
          dayIso,
          startIso: entry.startsAt,
          endIso: entry.endsAt,
          anchorRect,
          excludeKey,
          ...findConflicts(toRange(entry.startsAt, entry.endsAt, dayIso), day, excludeKey),
        })
      }
    },
    [technicianData, dayBlocksFor, toRange, pauseEdit],
  )

  const handlePopoverChange = useCallback(
    (startIso: string, endIso: string) => {
      const editing = pauseEdit.editing
      if (!editing) return
      const day = dayBlocksFor(editing.dayIso)
      pauseEdit.updateRange(
        startIso,
        endIso,
        findConflicts(toRange(startIso, endIso, editing.dayIso), day, editing.excludeKey),
      )
    },
    [dayBlocksFor, toRange, pauseEdit],
  )

  const handlePopoverSave = useCallback(() => {
    const td = technicianData
    const editing = pauseEdit.editing
    if (!editing || !td) return
    // The button is disabled in this state; guarding anyway keeps the rule in one place rather
    // than relying on the popover to enforce it.
    if (isBlocked(editing)) return

    if (editing.kind === 'break') {
      void commitMove(
        { kind: 'break', technicianId: Number(td.technicianId), startIso: editing.startIso },
        editing,
      )
    } else if (editing.id !== undefined) {
      void commitMove(
        {
          kind: 'absence',
          entryId: editing.id,
          startIso: editing.startIso,
          endIso: editing.endIso,
        },
        editing,
      )
    }
  }, [technicianData, pauseEdit, commitMove])

  const isPending = upsertBreak.isPending || updateEntry.isPending

  /** A break's length is fixed by the profile, so the popover moves it rather than resizing it. */
  const breakDurationMin = useMemo(() => {
    const editing = pauseEdit.editing
    if (!editing || editing.kind !== 'break') return undefined
    return (
      dayBlocksFor(editing.dayIso).breakBlock?.durationMinutes ??
      technicianData?.breakDurationMinutes
    )
  }, [pauseEdit.editing, dayBlocksFor, technicianData])

  return {
    editing: pauseEdit.editing,
    isPending,
    breakDurationMin,
    handleDrop,
    handleTap,
    handlePopoverChange,
    handlePopoverSave,
    close: pauseEdit.close,
  }
}
